import { ComponentProps, isValidElement } from "react";
import styled from "styled-components";

// TEXT

// STYLES
export const EllipsisContainer = styled.div<{ $lines?: number }>`
  ${({ $lines }) =>
    $lines
      ? `
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${$lines};
        overflow: hidden;
      `
      : `
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        * {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}
`;

// DATA

// TYPES
export type Props = ComponentProps<"div"> & {
  /**
   * The text component to wrap with ellipsis behavior
   */
  children: React.ReactNode;
  /**
   * Disable the automatic tooltip
   * @default false
   */
  disableTooltip?: boolean;
  /**
   * Maximum number of lines before truncating. Omit for single-line behaviour.
   */
  lines?: number;
};

// CONSTANTS

// FUNCTIONS
/**
 * Type guard to check if a value is an object with children property
 */
const hasChildren = (
  value: unknown,
): value is { children: React.ReactNode } => {
  return typeof value === "object" && value !== null && "children" in value;
};

/**
 * Recursively extracts text content from React nodes
 */
export const getTextContent = (node: React.ReactNode): string => {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (isValidElement(node) && hasChildren(node.props)) {
    return getTextContent(node.props.children);
  }
  if (Array.isArray(node)) {
    return node.map(getTextContent).join("");
  }
  return "";
};

/**
 * Checks if an element is overflowing (has ellipsis).
 *
 * Single-line mode uses white-space: nowrap, so text overflows horizontally —
 * detected via scrollWidth > clientWidth. The child element is also checked
 * because styled-components can render the visible text inside a nested element
 * rather than directly in the container.
 *
 * Multi-line mode uses -webkit-line-clamp, so text overflows vertically. Normally
 * you'd check scrollHeight > clientHeight, but -webkit-line-clamp collapses the
 * container's scrollHeight to match its visible cut-off height (determined by lines prop),
 * making that check always return false. Instead we check the child's natural full height against
 * the container's cut-off height.
 */
export const isOverflowing = (
  element: HTMLElement,
  lines?: number,
): boolean => {
  if (lines) {
    const child = element.firstElementChild as HTMLElement | null;
    if (child) return child.scrollHeight > element.clientHeight;
    return element.scrollHeight > element.clientHeight;
  }

  if (element.scrollWidth > element.clientWidth) return true;

  const textElement = element.querySelector("p, span, div, *:not(:empty)");
  if (textElement && textElement instanceof HTMLElement) {
    return textElement.scrollWidth > textElement.clientWidth;
  }

  return false;
};

// ASSETS
